import React, { useState } from "react";
import { useChatStore } from "../../store/chatStore";
import { SendIcon } from "../common/Icons"; // Import SendIcon
import useAuthStore from "../../store/authStore"; // Import useAuthStore
import stylesFromSidebar from "./ChatSidebarView.module.css"; // Import styles for consistency
import { logError } from "../../utils/logging"; // Import logging utility
import { trackEvent } from "../../services/analyticsService";
import { useGuidedFlowStore } from "../../store/guidedFlowStore";

// Basic styling for the input, can be moved to a CSS file
// This will be refactored in Task 3.1.6 and sub-tasks of 3.1.7
const ownStyles: { [key: string]: React.CSSProperties } = {
  // Renamed to avoid conflict
  chatInputContainer: {
    display: "flex",
    padding: "10px",
    borderTop: "1px solid #ccc",
    backgroundColor: "#f9f9f9",
    alignItems: "center", // Align items vertically for icon button
  },
  inputField: {
    flexGrow: 1,
    padding: "10px",
    border: "1px solid #ddd",
    borderRadius: "20px",
    marginRight: "10px",
    fontSize: "1rem",
  },
  sendButton: {
    padding: "8px", // Adjusted padding for icon button
    minWidth: "40px", // Ensure button has some width for the icon
    minHeight: "40px", // Ensure button has some height for the icon
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    backgroundColor: "var(--primary-color)", // Use CSS variable
    color: "var(--text-color-on-primary)", // Use CSS variable
    border: "none",
    borderRadius: "50%", // Make it circular
    cursor: "pointer",
  },
  sendButtonDisabled: {
    backgroundColor: "var(--background-color-disabled)", // Use CSS variable
    color: "var(--text-color-secondary)", // Use CSS variable
    cursor: "not-allowed",
  },
};

export const ChatInput: React.FC = () => {
  const [inputValue, setInputValue] = useState("");
  const sendMessage = useChatStore((state) => state.sendMessage);
  const isStreaming = useChatStore((state) => state.isStreaming);
  const generalError = useChatStore((state) => state.generalError);
  const streamError = useChatStore((state) => state.streamError);

  const {
    userStatus,
    isRateLimited,
    isAuthenticated,
    apiUrls,
    apiError,
    messageLimits, // Added messageLimits from authStore
  } = useAuthStore();

  const { status: guidedFlowStatus, abandonFlow } = useGuidedFlowStore();

  // Check if any error exists
  const hasError = generalError || streamError || apiError;

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    // If user starts typing in main input while a guided flow is active, abandon it.
    if (guidedFlowStatus === 'collecting') {
      abandonFlow();
    }
    setInputValue(e.target.value);
  };

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const messageText = inputValue.trim();
    if (!messageText || isStreaming) {
      // Prevent sending if empty or already streaming
      return;
    }

    try {
      // As per docs, currentPageUrl is optional. We can include it.
      const currentPageUrl = window.location.href;
      await sendMessage(messageText, currentPageUrl);
      setInputValue(""); // Clear input only on successful initiation
    } catch (e) {
      // Error is already set in the store by sendMessage action,
      // ChatInput doesn't need to do much more here unless specific UI changes are needed in ChatInput itself.
      logError("Error sending message from ChatInput:", e);
    }
  };

  const isDisabled = isStreaming || !inputValue.trim() || isRateLimited; // Also disable if rate limited

  // Determine specific UI states for rate limits / auth prompts
  // Note: isAuthenticated might be false for guests even if they can send some initial messages.
  // We rely on isRateLimited for guests if their initial quota is exhausted.
  // Or if an action explicitly requires login (not covered by this component directly yet).

  const guestNeedsLogin =
    userStatus === "guest" &&
    messageLimits?.max !== null &&
    messageLimits?.max !== undefined &&
    messageLimits?.current >= messageLimits.max;
  // A more explicit check for guest needing to log in for *any* message could be:
  // const guestNeedsLogin = userStatus === 'guest' && !isAuthenticated (if guests MUST login to send anything)
  // For now, guest rate limit implies login needed as per Sprint 2.1 designs.

  const basicUserNeedsUpgrade =
    userStatus === "loggedin_basic" &&
    messageLimits?.max !== null &&
    messageLimits?.max !== undefined &&
    messageLimits?.current >= messageLimits.max;

  const loginUrl = apiUrls?.loginUrl || "/wp-login.php";
  const upgradeUrl = apiUrls?.upgradeUrl || "/grupuri/plus/";

  // Specific check for authentication failure (e.g., expired JWT mid-session)
  const authFailedMidSession =
    !isAuthenticated &&
    apiError &&
    (apiError.includes("401") ||
      apiError.includes("403") ||
      apiError.toLowerCase().includes("authentication") ||
      apiError.toLowerCase().includes("unauthorized"));

  if (authFailedMidSession) {
    return (
      <div
        className={`${stylesFromSidebar.limitReachedMessage} ${ownStyles.chatInputContainer}`}
      >
        <p>
          {apiError ||
            "Sesiunea a expirat sau autentificarea a eșuat. Te rugăm să te autentifici din nou."}
        </p>
        <a
          href={loginUrl}
          className={stylesFromSidebar.actionButton}
          onClick={() => trackEvent("login_prompt_click")}
        >
          Autentificare
        </a>
      </div>
    );
  }

  if (guestNeedsLogin) {
    return (
      <div
        className={`${stylesFromSidebar.limitReachedMessage} ${ownStyles.chatInputContainer}`}
      >
        <p>Autentifică-te pentru a putea trimite mai multe mesaje.</p>
        <a
          href={loginUrl}
          className={stylesFromSidebar.actionButton}
          onClick={() => trackEvent("login_prompt_click")}
        >
          Autentificare
        </a>
      </div>
    );
  }

  if (basicUserNeedsUpgrade) {
    return (
      <div
        className={`${stylesFromSidebar.limitReachedMessage} ${ownStyles.chatInputContainer}`}
      >
        <p>
          Devino membru SMARTERS+ pentru a continua conversația și pentru
          funcționalități mai puternice.
        </p>
        <a
          href={upgradeUrl}
          className={stylesFromSidebar.actionButton}
          onClick={() => trackEvent("upgrade_prompt_click")}
        >
          Devino SMARTERS+
        </a>
      </div>
    );
  }

  // Default: show the input form
  return (
    <form onSubmit={handleSubmit} style={ownStyles.chatInputContainer}>
      <input
        type="text"
        value={inputValue}
        onChange={handleInputChange}
        placeholder={
          hasError
            ? apiError || generalError || streamError || "A apărut o eroare"
            : "Scrie aici întrebarea sau nevoia ta..."
        } // More specific error
        disabled={isStreaming || isRateLimited} // Disable input if streaming or rate limited
        style={ownStyles.inputField}
        aria-label="Câmp pentru mesaj chat"
      />
      <button
        type="submit"
        disabled={isDisabled}
        style={{
          ...ownStyles.sendButton,
          ...(isDisabled ? ownStyles.sendButtonDisabled : {}),
        }}
        aria-label={isStreaming ? "Se trimite mesajul..." : "Trimite mesaj"}
        title={isStreaming ? "Se trimite mesajul..." : "Trimite mesaj"}
      >
        {
          isStreaming ? (
            <span>...</span> /* Simple loading indicator for now */
          ) : (
            <SendIcon className="chat-input-send-icon" title="Trimite" />
          ) /* Use a simple class name */
        }
      </button>
    </form>
  );
};
